
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { lazy, Suspense, useEffect } from "react";
import { AppProvider } from "./components/AppProvider";
import Layout from "./components/Layout";
import { PerformanceDebugPanel } from "@/components/dev/PerformanceDebugPanel";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { UpdateNotification } from "./components/UpdateNotification";

import "./App.css";

// Lazy load all pages for better code splitting
const Index = lazy(() => import("./pages/Index"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Journal = lazy(() => import("./pages/Journal"));
const Settings = lazy(() => import("./pages/Settings"));
const Achievements = lazy(() => import("./pages/Achievements"));
const LandingPage = lazy(() => import("./pages/LandingPage"));
const Auth = lazy(() => import("./pages/Auth"));
const About = lazy(() => import("./pages/About"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Contact = lazy(() => import("./pages/Contact"));
const Accessibility = lazy(() => import("./pages/Accessibility"));
const Upgrade = lazy(() => import("./pages/Upgrade"));

const TransmissionsArchive = lazy(() => import("./pages/TransmissionsArchive"));
const TransmissionPost = lazy(() => import("./pages/TransmissionPost"));
const LiftingTracker = lazy(() => import("./pages/LiftingTracker"));
const Monolith = lazy(() => import("./pages/Monolith"));
const Suspended = lazy(() => import("./pages/Suspended"));

// Separate admin panel into its own chunk with preload capability
const AdminPanel = lazy(() => 
  import("./pages/AdminPanel").then(module => {
    // Preload admin dependencies when loading admin panel
    import('recharts');
    return module;
  })
);

// Lightweight loading component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[60vh]">
    <div className="text-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-4"></div>
      <p>Loading...</p>
    </div>
  </div>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000,   // 10 minutes
      refetchOnWindowFocus: false, // Prevent unnecessary refetches
      retry: 1, // Reduce retry attempts
    },
  },
});

// Scroll to top component that runs on route changes
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    // Always scroll to top when route changes
    window.scrollTo(0, 0);
    
    // Also scroll the body to top in case there are any nested scroll containers
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
  }, [pathname]);

  return null;
}

// Global scroll reset on app load
function GlobalScrollReset() {
  useEffect(() => {
    // Force scroll to top on app load/reload
    const resetScroll = () => {
      window.scrollTo(0, 0);
      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
    };
    
    // Run immediately
    resetScroll();
    
    // Also run after a short delay to catch any late-loading content
    const timer = setTimeout(resetScroll, 100);
    
    return () => clearTimeout(timer);
  }, []);

  return null;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <BrowserRouter>
          <AppProvider>
              <GlobalScrollReset />
              <ScrollToTop />
              <Layout>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="/" element={<Index />} />
            <Route path="/landing" element={<LandingPage />} />
            <Route path="/transmissions" element={<TransmissionsArchive />} />
            <Route path="/transmissions/:slug" element={<TransmissionPost />} />
                  <Route path="/auth" element={<Auth />} />
                  
                  {/* Protected App Routes */}
                  <Route
                    path="/dashboard"
                    element={
                      <ProtectedRoute>
                        <Dashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/journal"
                    element={
                      <ProtectedRoute>
                        <Journal />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/settings"
                    element={
                      <ProtectedRoute>
                        <Settings />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/achievements"
                    element={
                      <ProtectedRoute>
                        <Achievements />
                      </ProtectedRoute>
                    }
                  />
                  {/* Social Feed removed - redirect to dashboard */}
                  <Route path="/social-feed" element={<Navigate to="/dashboard" replace />} />
                  {/* HIDDEN FOR BETA - Premium system rollout pending */}
                  <Route
                    path="/upgrade"
                    element={<Navigate to="/dashboard" replace />}
                  />
                  <Route
                    path="/lifting"
                    element={
                      <ProtectedRoute>
                        <LiftingTracker />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/monolith"
                    element={
                      <ProtectedRoute>
                        <Monolith />
                      </ProtectedRoute>
                    }
                  />
                  
                  {/* Admin Routes */}
                  <Route
                    path="/admin"
                    element={
                      <ProtectedRoute requireAdmin={true}>
                        <AdminPanel />
                      </ProtectedRoute>
                    }
                  />
                  
                  {/* Public Information Pages */}
                  <Route path="/about" element={<About />} />
                  <Route path="/terms" element={<Terms />} />
                  <Route path="/privacy" element={<Privacy />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/accessibility" element={<Accessibility />} />
                  <Route path="/suspended" element={<Suspended />} />
                </Routes>
              </Suspense>
              </Layout>
              {import.meta.env.DEV && <PerformanceDebugPanel />}
              <UpdateNotification />
              <Toaster />
              <Sonner />
          </AppProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
